import React, { useEffect, useRef, useState, useMemo } from "react";
import { companyList, companyCategory } from "../../constants/companies";
import linkArrow from "../../assets/arrows/link_arrow.svg";
import "./clientCarousel.css";

const ClientCarousel = ({ sectionRef }) => {
  const imgRef = useRef({}); // Changed to an object dictionary map to prevent dynamic array index shifting
  const carouselRef = useRef(null);
  const footerRef = useRef(null);

  const [active, setActive] = useState("");
  const [activeCate, setActiveCate] = useState("Innovation");
  const [companyDetail, setCompanyDetail] = useState({});
  const [mouseOnCarousel, setMouseOnCarousel] = useState("");

  // Clean layout helper for category normalized matching
  const normalizedCategories = useMemo(() => {
    return companyCategory.map((cate) => ({
      original: cate,
      normalized: cate.replace(/ /g, "").toLowerCase(),
    }));
  }, []);

  const handleCarouselScroll = (data) => {
    setActiveCate(data);
    const targetId = data.replace(/ /g, "");
    // Querying by the explicit data-anchor attribute ensures we grab the first card of the group
    const showImage = document?.querySelector(`[data-anchor="${targetId}"]`);
    if (showImage) {
      showImage.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  // Dynamically attach or detach elements to our ref tracker safely
  const setImgRef = (key, el) => {
    if (el) {
      imgRef.current[key] = el;
    } else {
      delete imgRef.current[key];
    }
  };

  // IntersectionObserver for active text & active categories
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: Array.from({ length: 20 }, (_, idx) => idx * 0.05),
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const textDiv = entry.target.querySelector(".carouselCard--text-liner");

        if (entry.intersectionRatio > 0.8) {
          if (textDiv) textDiv.style.opacity = "1";

          const compName = entry.target.getAttribute("data-name");
          const cate = entry.target.getAttribute("data-category");

          setActiveCate(cate);

          const innerImg = entry.target.querySelector(".carouselCard--img");
          if (innerImg && innerImg.getAttribute) {
            setActive(innerImg.getAttribute("data-id") || "");
          }

          // Direct map lookup
          const categoryCompanies = companyList[cate] || [];
          const currentCompany = categoryCompanies.find(
            (c) => c.name === compName,
          );
          if (currentCompany) {
            setCompanyDetail(currentCompany);
          }
        } else {
          if (textDiv) textDiv.style.opacity = "0";
        }
      });
    }, options);

    // Grab all safely stored active elements from our ref collection
    const validRefs = Object.values(imgRef.current).filter(Boolean);
    validRefs.forEach((ref) => observer.observe(ref));

    return () => observer.disconnect();
  }, []); // Safely mounts once and relies on static raw element targets

  // Scoped Mouse Hover Tracking
  const handleMouseMove = (event) => {
    if (!carouselRef.current) return;

    const rect = carouselRef.current.getBoundingClientRect();
    const { clientX: x, clientY: y } = event;

    if (
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom
    ) {
      const width = rect.right - rect.left;
      const relativeX = x - rect.left;

      if (relativeX < width * 0.3) {
        if (mouseOnCarousel !== "left") setMouseOnCarousel("left");
      } else if (relativeX > width * 0.7) {
        if (mouseOnCarousel !== "right") setMouseOnCarousel("right");
      } else {
        if (mouseOnCarousel !== "center") setMouseOnCarousel("center");
      }
    } else {
      if (mouseOnCarousel !== "") setMouseOnCarousel("");
    }
  };

  const handleMouseLeave = () => {
    setMouseOnCarousel("");
  };

  // Footers visibility tracker
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (footerRef.current) {
          footerRef.current.style.opacity =
            entry.boundingClientRect.top > 0 && entry.isIntersecting
              ? "1"
              : "0";
        }
      },
      {
        root: null,
        threshold: Array.from({ length: 20 }, (_, idx) => idx * 0.05),
      },
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [sectionRef]);

  const handleCarouselClick = () => {
    if (!carouselRef.current || !carouselRef.current.childNodes[0]) return;
    // const cardWidth =
    //   carouselRef.current.childNodes[0].getBoundingClientRect().width;
    const boxWidth = carouselRef.current.getBoundingClientRect().width;
    const gap =
      carouselRef.current.childNodes[1].getBoundingClientRect().left -
      carouselRef.current.childNodes[0].getBoundingClientRect().right;
    const imgWidth =
      carouselRef.current.childNodes[1].getBoundingClientRect().width;
    const cardWidth = boxWidth - (boxWidth - imgWidth - gap);

    if (mouseOnCarousel === "left") {
      carouselRef.current.scrollLeft -= cardWidth;
    } else if (mouseOnCarousel === "right") {
      carouselRef.current.scrollLeft += cardWidth;
    }
  };

  // console.log(carouselRef.current.getBoundingClientRect().width);
  return (
    <div className="clientCarousel" ref={sectionRef} id="clients">
      <div className="clientCarousel__container">
        {/* Selection List */}
        <div className="clientCarousel__selector">
          <div className="clientCarousel__selector--list">
            <div className="selector__list--header">
              <p>Stage</p>
            </div>
            {normalizedCategories.map((cate, index) => (
              <div
                onMouseEnter={() => handleCarouselScroll(cate.original)}
                className={`selector__list ${active.toLowerCase() === cate.normalized ? "active" : ""}`}
                key={index}
              >
                <p className="p__ppmori">{cate.original}</p>
                <div />
              </div>
            ))}
          </div>
        </div>

        {/* Carousel Box */}
        <div className="clientCarousel__carousel">
          <div className="clientCarousel__carousel--label">
            <p>{activeCate === "AI" ? "AI/ML" : activeCate}</p>
          </div>
          <div
            className={`clientCarousel__carousel--box mouse${mouseOnCarousel.charAt(0).toUpperCase() + mouseOnCarousel.slice(1)}`}
            ref={carouselRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleCarouselClick}
          >
            {companyCategory.map((categoryName) => {
              const companies = companyList[categoryName] || [];
              const htmlTargetId = categoryName.replace(/ /g, "");

              return companies.map((comp, compIndex) => (
                <div
                  className="carouselCard"
                  data-category={categoryName}
                  data-name={comp.name}
                  /* Apply anchor identifier ONLY to the first item in a group loop */
                  data-anchor={compIndex === 0 ? htmlTargetId : undefined}
                  key={`${categoryName}-${comp.name}-${compIndex}`}
                  ref={(el) => setImgRef(`${categoryName}-${comp.name}`, el)}
                >
                  <img
                    className="carouselCard--img"
                    src={comp.imgUrl}
                    alt={comp.name}
                    data-id={
                      htmlTargetId
                    } /* Replaced native duplicate ID with safe data attribute */
                  />
                  <div
                    className="carouselCard--text-liner"
                    style={{ opacity: 0, transition: "opacity 0.3s" }}
                  >
                    <img
                      className="carouselCard--logo"
                      src={comp.logo}
                      alt={`${comp.name} logo`}
                    />
                    <p className="p__ppmori">{comp.liner}</p>
                  </div>
                </div>
              ));
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="clientCarousel__footer"
        ref={footerRef}
        style={{ opacity: 0, transition: "opacity 0.3s" }}
      >
        <div className="clientCarousel__footer--square">
          <div />
          <p>{companyDetail?.name || ""}</p>
        </div>
        <div className="clientCarousel__footer--content">
          <p className="p__ppmori">
            {companyDetail?.subText || ""}
            {companyDetail?.subText && <span>•</span>}
          </p>
          {companyDetail?.url && (
            <a
              href={companyDetail.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              Learn More
            </a>
          )}
          <img src={linkArrow} alt="arrow" />
        </div>
      </div>
    </div>
  );
};

export default ClientCarousel;
